import { useEffect, useRef, useState } from 'react'
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import BackHeader from '../../components/BackHeader/BackHeader'
import { formatMessageTime, nextMatch, reminderMatch } from './logic'
import { findTeam, useHold, useTeamState } from './store'
import Loading from '../../components/Loading/Loading'
import { Composer, HoldScreen, PlaceLink, SquadNeed, SpotLine, TeamMark, matchBackdrop } from './bits'
import MatchViews from './MatchViews'
import EventViews from './EventViews'
import WallView, { TeamWall } from './WallView'
import FeeView, { FeeReminder, teamIdFromPath } from './FeeView'
import FineView from './FineView'
import MembersView from './MembersView'
import SettingsView from './SettingsView'
import InviteView from './InviteView'
import './Fodboldhold.css'

function UnpaidFeeReminder() {
  const { pathname } = useLocation()
  const { state } = useTeamState()
  const teamId = teamIdFromPath(pathname)
  const team = teamId ? findTeam(state, teamId) : null
  const season = team?.seasons.find((item) => item.current) || null
  return <FeeReminder team={team} season={season} meId={state.meId} active={Boolean(teamId)} />
}

export default function Fodboldhold() {
  return (
      <>
      <UnpaidFeeReminder />
      <Routes>
        <Route index element={<TeamList />} />
        <Route path="inviter/:token" element={<InviteView />} />
        <Route path="tilmeld/:inviteCode" element={<JoinTeam />} />
        <Route path=":teamId" element={<TeamHome />} />
        <Route path=":teamId/kampe" element={<MatchViews list />} />
        <Route path=":teamId/kamp/:matchId" element={<MatchViews />} />
        <Route path=":teamId/arrangementer" element={<EventViews list />} />
        <Route path=":teamId/arrangement/:eventId" element={<EventViews />} />
        <Route path=":teamId/vaeg" element={<WallView />} />
        <Route path=":teamId/topscorer" element={<TopscorerRedirect />} />
        <Route path=":teamId/kontingent" element={<FeeView />} />
        <Route path=":teamId/boedekasse" element={<FineView />} />
        <Route path=":teamId/medlemmer" element={<MembersView />} />
        <Route path=":teamId/indstillinger" element={<SettingsView />} />
      </Routes>
      </>
  )
}

function HomeFeed({ team, dispatch, nameOf, meId }) {
  const posts = team.posts || []
  const news = team.news || []
  const stream = [
    ...news.map((item) => ({ kind: 'news', id: item.id, at: item.createdAt, item })),
    ...posts.map((item) => ({ kind: 'post', id: item.id, at: item.createdAt, item })),
  ].sort((a, b) => new Date(b.at) - new Date(a.at))

  return (
    <section className="hold-feed" aria-label="Væggen">
      <Composer
        allowImage
        placeholder="Skriv et opslag til holdet"
        onSubmit={(text, image) => dispatch({ type: 'ADD_POST', teamId: team.id, text, image })}
      />
      {stream.length === 0 ? (
        <p className="hold-empty">Nyheder og opslag lander her.</p>
      ) : (
        <div className="hold-feed-stream">
          {stream.map((entry) => entry.kind === 'news' ? (
            <NewsLine key={entry.id} item={entry.item} />
          ) : (
            <TeamWall
              key={entry.id}
              team={team}
              posts={[entry.item]}
              dispatch={dispatch}
              nameOf={nameOf}
              meId={meId}
              showComposer={false}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function NewsLine({ item }) {
  const body = (
    <>
      <span className="hold-feed-news-top">
        <span className="hold-feed-kicker">Nyhed</span>
        <time dateTime={item.createdAt}>{formatMessageTime(item.createdAt)}</time>
      </span>
      <span className="hold-feed-news-text">{item.text}</span>
    </>
  )
  return item.path ? <Link className="hold-feed-news" to={item.path}>{body}</Link> : <div className="hold-feed-news">{body}</div>
}

function TopscorerRedirect() {
  const { teamId } = useParams()
  return <Navigate to={`/hold/${teamId}/kampe?fane=topscorer`} replace />
}

function JoinTeam() {
  const { inviteCode } = useParams()
  const navigate = useNavigate()
  const { dispatch } = useTeamState()
  const started = useRef(false)
  const [message, setMessage] = useState('Du bliver medlem…')

  useEffect(() => {
    if (started.current) return
    started.current = true
    dispatch({ type: 'JOIN', inviteCode }).then((result) => {
      if (result?.ok && result.teamId) {
        navigate(`/hold/${result.teamId}`, { replace: true })
        return
      }
      setMessage(result?.error || 'Invitationen virkede ikke.')
    })
  }, [dispatch, inviteCode, navigate])

  return (
    <HoldScreen>
      <p className="hold-empty">{message}</p>
    </HoldScreen>
  )
}

function TeamList() {
  const navigate = useNavigate()
  const { state } = useTeamState()
  if (state.loading) {
    return <HoldScreen><Loading fill /></HoldScreen>
  }
  if (state.teams.length === 1) {
    return <Navigate to={`/hold/${state.teams[0].id}`} replace />
  }

  return (
    <HoldScreen>
      <BackHeader eyebrow="Svirr" title="Hold" onBack={() => navigate('/')} />
      {state.teams.length === 0 ? <p className="hold-empty">Du er ikke med på et hold endnu.</p> : null}
      <div className="hold-stack">
        {state.teams.map((team) => {
          const mine = team.members.find((member) => member.id === state.meId)
          return (
            <Link key={team.id} className="hold-card hold-link-card hold-team-card" to={`/hold/${team.id}`}>
              <TeamMark team={team} className="hold-logo" />
              <span>
                <strong>{team.name}</strong>
                <p className="hold-meta">{mine?.isAdmin ? 'Hold-admin' : 'Medlem'}</p>
              </span>
            </Link>
          )
        })}
      </div>
    </HoldScreen>
  )
}

function clockFace(iso) {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return { time: '–', day: '' }
  return {
    time: new Intl.DateTimeFormat('da-DK', { hour: '2-digit', minute: '2-digit' }).format(date),
    day: new Intl.DateTimeFormat('da-DK', { weekday: 'short', day: 'numeric', month: 'short' }).format(date),
  }
}

function meetingFace(value, kickoff) {
  if (!value) return null
  if (/^\d{2}:\d{2}$/.test(value)) return { time: value, day: 'Samme dag' }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  const start = new Date(kickoff)
  const sameDay = !Number.isNaN(start.getTime()) && date.toDateString() === start.toDateString()
  return {
    time: new Intl.DateTimeFormat('da-DK', { hour: '2-digit', minute: '2-digit' }).format(date),
    day: sameDay
      ? 'Samme dag'
      : new Intl.DateTimeFormat('da-DK', { weekday: 'short', day: 'numeric', month: 'short' }).format(date),
  }
}

function UpcomingMatch({ match, teamId, soon, backdrop }) {
  const kick = clockFace(match.kickoff)
  const meet = meetingFace(match.meeting, match.kickoff)
  const to = `/hold/${teamId}/kamp/${match.id}`

  return (
    <article className="hold-fixture hold-next">
      <div className="hold-next-bg" aria-hidden="true">
        <img src={backdrop} alt="" />
      </div>
      <Link className="hold-next-open" to={to}>
        <div className="hold-next-top">
          <span className={`hold-next-kicker${soon ? ' is-soon' : ''}`}>{soon ? 'Om 1 time' : 'Næste kamp'}</span>
          <SpotLine item={match} />
        </div>
        <strong className="hold-next-title">{match.home ? 'Hjemme' : 'Ude'} mod {match.opponent}</strong>
        <div className={`hold-fixture-clocks${meet ? '' : ' is-single'}`}>
          <div>
            <span>Kampstart</span>
            <strong>{kick.time}</strong>
            <em>{kick.day}</em>
          </div>
          {meet ? (
            <div className="is-meet">
              <span>Mødetid</span>
              <strong>{meet.time}</strong>
              <em>{meet.day}</em>
            </div>
          ) : null}
        </div>
      </Link>
      {match.place ? (
        <div className="hold-fixture-place">
          <Link className="hold-next-place" to={to}>{match.place}</Link>
          <PlaceLink place={match.place} tiny />
        </div>
      ) : null}
      <Link className="hold-next-open" to={to}>
        <SquadNeed count={match.attendees.length} max={match.max} />
      </Link>
    </article>
  )
}

function TeamHome() {
  const navigate = useNavigate()
  const { state } = useTeamState()
  const { team, current, isAdmin, meId, loading, dispatch, nameOf } = useHold()
  if (loading && !team) return <HoldScreen><Loading fill /></HoldScreen>
  if (!team || !current) return <HoldScreen><p className="hold-empty">Holdet findes ikke.</p></HoldScreen>

  const upcoming = nextMatch(current)
  const reminder = reminderMatch(current, meId)

  const links = [
    ['Kampe', 'kampe'],
    ['Arrangementer', 'arrangementer'],
    ['Kontingent', 'kontingent'],
    ['Bødekasse', 'boedekasse'],
    ['Medlemmer', 'medlemmer'],
  ]

  return (
    <HoldScreen>
      <BackHeader
        eyebrow="Hold"
        title={team.name}
        subtitle={current.name}
        onBack={() => navigate(state.teams.length > 1 ? '/hold' : '/')}
        actions={isAdmin ? (
          <Link className="hold-settings-button" to={`/hold/${team.id}/indstillinger`} aria-label="Indstillinger">
            <SettingsOutlinedIcon />
          </Link>
        ) : null}
      />
      <TeamMark team={team} className="hold-home-logo" />

      {upcoming ? (
        <UpcomingMatch match={upcoming} teamId={team.id} soon={reminder?.id === upcoming.id} backdrop={matchBackdrop(current.matches, upcoming)} />
      ) : (
        <p className="hold-empty">Ingen kommende kampe i denne sæson.</p>
      )}

      {reminder && reminder.id !== upcoming?.id ? (
        <UpcomingMatch match={reminder} teamId={team.id} soon backdrop={matchBackdrop(current.matches, reminder)} />
      ) : null}

      <nav className="hold-grid" aria-label="Holdets sider">
        {links.map(([label, path]) => (
          <Link key={path} to={`/hold/${team.id}/${path}`}>{label}</Link>
        ))}
        {current.standingsUrl ? (
          <a href={current.standingsUrl} target="_blank" rel="noopener noreferrer">Stilling</a>
        ) : null}
      </nav>

      <HomeFeed team={team} dispatch={dispatch} nameOf={nameOf} meId={meId} />

    </HoldScreen>
  )
}
